import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ServerCertView } from '@1qa/shared';
import { Plus, ShieldCheck, Trash2 } from '@/components/icons';
import { api, errorMessage } from '@/lib/api';
import { Badge, Button, Card, CardBody, CardHeader, Field, Input, Spinner, Textarea, useConfirm, useToast } from '@/components/ui';

/**
 * Server certificates of a performance project, for recording a phone's traffic: the Device Agent opens these hosts with
 * the server's own certificate, so testers install nothing on the phone and any build of the app works (release builds,
 * certificate pinning). Everyone in the project sees the hosts; Project Admins add and remove certificates.
 */
export function ServerCertsCard({ projectId, canEdit }: { projectId: string; canEdit: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const q = useQuery({ queryKey: ['server-certs', projectId], queryFn: () => api.serverCerts(projectId) });
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [certificate, setCertificate] = useState('');
  const [privateKey, setPrivateKey] = useState('');
  const reset = () => {
    setAdding(false);
    setName('');
    setCertificate('');
    setPrivateKey('');
  };
  const add = useMutation({
    mutationFn: () => api.addServerCert(projectId, { name: name.trim() || undefined, certificate, privateKey }),
    onSuccess: (c) => {
      toast.success(`Certificate for ${c.hosts.join(', ')} added`);
      reset();
      qc.invalidateQueries({ queryKey: ['server-certs', projectId] });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.deleteServerCert(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['server-certs', projectId] }),
    onError: (e) => toast.error(errorMessage(e)),
  });

  return (
    <Card className="lg:col-span-3">
      <CardHeader
        title="Recording on a phone: server certificates"
        description="Add your test environment’s API certificate and key, and phones record its traffic with nothing to install: no 1QA certificate on the phone, no special build of the app."
        actions={
          canEdit &&
          !adding && (
            <Button size="sm" variant="secondary" icon={<Plus className="size-3" />} onClick={() => setAdding(true)}>
              Add certificate
            </Button>
          )
        }
      />
      <CardBody className="space-y-4">
        {q.isPending ? (
          <Spinner />
        ) : q.isError ? (
          <p className="text-sm text-danger-text">{errorMessage(q.error)}</p>
        ) : q.data.length === 0 ? (
          <p className="text-sm text-fg-muted">
            None yet. Without one, testers install the 1QA certificate on their phone and record with a debug build of the app.
          </p>
        ) : (
          <ul className="space-y-2">
            {q.data.map((c) => (
              <CertRow
                key={c.id}
                cert={c}
                canEdit={canEdit}
                onRemove={async () => {
                  const ok = await confirm({
                    title: 'Remove this certificate?',
                    message: `Phones recording ${c.hosts.join(', ')} will need the 1QA certificate again. The key is deleted from 1QA.`,
                    confirmLabel: 'Remove certificate',
                  });
                  if (ok) remove.mutate(c.id);
                }}
              />
            ))}
          </ul>
        )}
        {adding && (
          <form
            className="space-y-4 rounded-field p-4 ring-1 ring-border ring-inset"
            onSubmit={(e) => {
              e.preventDefault();
              add.mutate();
            }}
          >
            <p className="rounded-field bg-warning-subtle p-3 text-sm text-fg ring-1 ring-warning/40 ring-inset">
              Use certificates of test environments only, never production. The key is stored encrypted and is sent only to the Device Agent of
              someone recording into this project, so anyone who can record here with their own agent could obtain it.
            </p>
            <Field label="Name" hint="Optional, for example “Staging API”. The certificate’s first host otherwise.">
              {(id) => <Input id={id} value={name} onChange={(e) => setName(e.target.value)} maxLength={100} />}
            </Field>
            <Field label="Certificate (PEM)" hint="The server’s certificate first, then its intermediate certificates, each starting with -----BEGIN CERTIFICATE-----.">
              {(id) => <Textarea id={id} value={certificate} onChange={(e) => setCertificate(e.target.value)} rows={6} className="font-mono text-xs" spellCheck={false} />}
            </Field>
            <Field
              label="Private key (PEM)"
              hint="Unencrypted, starting with -----BEGIN PRIVATE KEY-----. A .pfx / .p12 file converts with: openssl pkcs12 -in server.pfx -nodes -out server.pem (then paste both parts)."
            >
              {(id) => <Textarea id={id} value={privateKey} onChange={(e) => setPrivateKey(e.target.value)} rows={4} className="font-mono text-xs" spellCheck={false} autoComplete="off" />}
            </Field>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={reset} disabled={add.isPending}>
                Cancel
              </Button>
              <Button type="submit" loading={add.isPending} disabled={!certificate.trim()}>
                Add certificate
              </Button>
            </div>
          </form>
        )}
      </CardBody>
    </Card>
  );
}

function CertRow({ cert, canEdit, onRemove }: { cert: ServerCertView; canEdit: boolean; onRemove: () => void }) {
  return (
    <li className="flex items-start gap-3 rounded-field bg-surface-sunken px-3 py-2 text-sm">
      <ShieldCheck className="mt-0.5 size-4 shrink-0 text-fg-muted" aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="flex flex-wrap items-center gap-2">
          <span className="font-medium text-fg">{cert.name}</span>
          {cert.expired ? <Badge tone="danger">Expired</Badge> : cert.coversAppHost ? <Badge tone="success">Covers the app address</Badge> : null}
        </p>
        <p className="break-words text-fg-secondary">{cert.hosts.join(', ')}</p>
        <p className="text-xs text-fg-muted">
          Valid until {cert.notAfter.slice(0, 10)} · {cert.issuer}
        </p>
      </div>
      {canEdit && <Button variant="ghost" size="sm" aria-label={`Remove the certificate ${cert.name}`} icon={<Trash2 className="size-3" />} onClick={onRemove} />}
    </li>
  );
}
